import { z } from 'zod';

export const reportFormatSchema = z.enum(['csv', 'json', 'xlsx', 'pdf']);
export const reportParameterSchema = z.object({
  name: z.string(),
  type: z.enum(['date', 'integer', 'boolean', 'enum', 'string']),
  required: z.boolean().optional(),
  label: z.string().optional(),
  default: z.union([z.string(), z.number(), z.boolean()]).optional(),
  options: z.array(z.string()).optional(),
  min: z.number().optional(),
  max: z.number().optional(),
});
export const reportDefinitionSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  category: z.enum(['operational', 'financial', 'regulatory', 'compliance']),
  parameters: z.array(reportParameterSchema),
  formats: z.array(reportFormatSchema),
  can_generate: z.boolean(),
  can_export: z.boolean(),
  can_schedule: z.boolean().default(false),
  can_view_schedules: z.boolean().default(false),
});
export const reportExecutionSchema = z.object({
  id: z.string().uuid(),
  report_definition_id: z.string().uuid(),
  report_name: z.string(),
  status: z.enum(['pending', 'queued', 'processing', 'completed', 'failed', 'cancelled']),
  created_at: z.string(),
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  row_count: z.number().nullable(),
  duration_ms: z.number().nullable(),
  data_size_bytes: z.number().nullable(),
  error_message: z.string().nullable(),
  can_export: z.boolean(),
  outputs: z.array(
    z.object({
      id: z.string().uuid(),
      format: reportFormatSchema,
      status: z.enum(['pending', 'generating', 'completed', 'failed']),
      size_bytes: z.number().nullable(),
    })
  ),
});
const paginationSchema = z
  .object({ total_pages: z.number(), total_count: z.number() })
  .passthrough();
export const reportCatalogResponseSchema = z.object({
  success: z.literal(true),
  data: z.array(reportDefinitionSchema),
  meta: paginationSchema,
});
export const reportHistoryResponseSchema = z.object({
  success: z.literal(true),
  data: z.array(reportExecutionSchema),
  meta: paginationSchema,
});
export const reportDetailResponseSchema = z.object({
  success: z.literal(true),
  data: reportDefinitionSchema,
});
export const reportExecutionResponseSchema = z.object({
  success: z.literal(true),
  data: reportExecutionSchema,
});
export const reportQueuedResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    execution_id: z.string().uuid(),
    report_definition_id: z.string().uuid(),
    status: reportExecutionSchema.shape.status,
  }),
});
export type ReportDefinition = z.infer<typeof reportDefinitionSchema>;
export type ReportExecution = z.infer<typeof reportExecutionSchema>;
export type ReportFormat = z.infer<typeof reportFormatSchema>;
export type ReportParameterValues = Record<string, string | number | boolean>;

export const reportScheduleInputSchema = z.object({
  report_definition_id: z.string().uuid(),
  name: z.string().trim().max(200).optional(),
  frequency: z.enum(['daily', 'weekly', 'monthly']),
  format: reportFormatSchema,
  timezone: z.string().trim().min(1).max(100),
  hour: z.number().int().min(0).max(23),
  period_length_days: z.number().int().min(1).max(366),
  day_of_week: z.number().int().min(0).max(6),
  day_of_month: z.number().int().min(1).max(28),
});
export const reportScheduleSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  report_definition_id: z.string().uuid(),
  report_name: z.string(),
  status: z.enum(['active', 'paused', 'disabled']),
  frequency: z.enum(['daily', 'weekly', 'monthly']),
  format: reportFormatSchema,
  timezone: z.string(),
  hour: z.number().int(),
  day_of_week: z.number().int(),
  day_of_month: z.number().int(),
  period_length_days: z.number().int(),
  next_run_at: z.string().nullable(),
  last_run_at: z.string().nullable(),
  run_count: z.number().int(),
  success_count: z.number().int(),
  failure_count: z.number().int(),
  can_manage: z.boolean(),
});
export const reportScheduleListResponseSchema = z.object({
  success: z.literal(true),
  data: z.array(reportScheduleSchema),
});
export const reportScheduleResponseSchema = z.object({
  success: z.literal(true),
  data: reportScheduleSchema,
});
export type ReportScheduleInput = z.infer<typeof reportScheduleInputSchema>;
export type ReportSchedule = z.infer<typeof reportScheduleSchema>;
